<!-- eslint-disable vuejs-accessibility/label-has-for -->
<template>
  <div
    :class="classes"
    :style="[styles, calculatedStyles]"
  >
    <div
      class="input-container accessibility-outline outline-inset"
      :aria-disabled="disabled"
      :aria-required="isRequired"
      :aria-readonly="isReadOnly"
      :aria-describedby="inputAriaDescribedby"
      :aria-invalid="hasError"
      :aria-label="ariaLabel || sanitizedLabel || 'Auswahl'"
      :aria-placeholder="placeholderText"
    >
      <div class="input-container-inner">
        <div
          v-if="iconLeft"
          class="icon-left"
        >
          <span>
            <i :class="iconLeft" />
          </span>
        </div>
        <span class="divider-left" />
        <div
          class="input-field"
          @click="$refs.input.focus()"
        >
          <span
            v-if="placeholder && !hasValue"
            class="placeholder"
          >{{ placeholder }}</span>
          <label :for="name">{{ label }}</label>
          <textarea
            ref="input"
            :name="name"
            :value="value"
            :rows="rows"
            :required="isRequired"
            :readonly="readonly"
            :disabled="disabled"
            :autocomplete="autocomplete"
            :minlength="minLength"
            :maxlength="maxLength"
            :aria-label="ariaLabel || sanitizedLabel"
            :aria-describedby="inputAriaDescribedby"
            :aria-invalid="hasError"
            :aria-required="isRequired"
            :aria-readonly="isReadOnly"
            :aria-disabled="disabled"
            @input="emitInput($event.target.value)"
            @focusin="focused = true"
            @focusout="onFocusOut($event)"
          />
        </div>
        <span class="divider-right" />
        <div
          v-if="hasValue || getIconRight"
          class="icon-right accessibility-outline outline-inset"
          :role="hasValue && clearable ? 'button' : undefined"
          :tabindex="hasValue && clearable ? 0 : -1"
          :aria-label="ariaLabel || (hasValue && clearable ? 'Clear' : undefined)"
          :aria-disabled="disabled"
          @click="hasValue && clearable ? clear($event, { val: '' }) : ''"
          @keydown.enter.prevent="hasValue && clearable ? clear($event, { val: '' }) : ''"
          @keydown.space.prevent="hasValue && clearable ? clear($event, { val: '' }) : ''"
        >
          <span v-if="hasValue && clearable">
            <span><i class="fa-thin fa-times" /></span>
          </span>
          <span v-else>
            <span><i :class="getIconRight" /></span>
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="hasError || counter || hint"
      class="input-hint"
    >
      <div v-if="hint">
        {{ hint }}
      </div>
      <div
        v-if="hasError"
        class="input-hint--error"
        v-html="errorMessage"
      />
      <div
        v-if="counter"
        class="input-hint--counter"
      >
        <span>{{ hasValue ? value.length : 0 }} {{ maxLength > 0 ? ` | ${maxLength}` : '' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import BaseInputMixin from '../../../shared/mixins/BaseInputMixin';

export default {
  name: 'BaseTextarea',
  mixins: [
    BaseInputMixin,
  ],
  model: {
    prop: 'value',
    event: 'update',
  },
  props: {
    value: {
      type: [String, Number],
      required: false,
      default: '',
    },
    minLength: {
      type: [Number, String],
      required: false,
      default: () => null,
    },
    maxLength: {
      type: [Number, String],
      required: false,
      default: () => null,
    },
    rows: {
      type: [Number, String],
      required: false,
      default: 3,
    },
    maxRows: {
      type: [Number, String],
      required: false,
      default: 8,
    },
    ariaLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      height: 0,
      textareaLineHeight: 0,
    };
  },
  computed: {
    calculatedStyles: {
      get() {
        return {
          '--textarea-height': `${this.height}px`,
          '--textarea-max-height': `${this.textareaLineHeight * this.maxRows}px`,
        };
      },
      set({ height }) {
        this.height = height;
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.textareaLineHeight = this.calculateHeight() || 0;
    });
  },
  methods: {
    emitInput(val) {
      this.$nextTick(() => {
        this.calculateHeight();
      });

      if (this.maxLength !== null && val.length > parseFloat(this.maxLength)) {
        this.emitError('max-length', val);
      } else if (this.minLength !== null && val.length < parseFloat(this.minLength)) {
        this.emitError('min-length', val);
      } else {
        this.emitSuccess(val);
      }

      this.emitUpdate(val);
    },
    calculateHeight() {
      const TEXTAREA = this.$refs.input;
      const LINE_HEIGHT = parseFloat(window.getComputedStyle(TEXTAREA).lineHeight);
      const LINE_COUNT = TEXTAREA.value.substr(0, TEXTAREA.selectionStart).split('\n').length;
      const ROWS = LINE_COUNT < this.rows ? this.rows : LINE_COUNT;

      this.calculatedStyles = {
        height: LINE_HEIGHT * ROWS > LINE_HEIGHT * this.maxRows
          ? LINE_HEIGHT * this.maxRows
          : LINE_HEIGHT * ROWS,
      };

      return LINE_HEIGHT;
    },
    ClearHook() {
      this.calculatedStyles = { height: this.textareaLineHeight * this.rows };
    },
    ClassesHook() {
      return {
        [`${this.classDefault}--min-length`]: this.minLength !== null,
        [`${this.classDefault}--max-length`]: this.maxLength !== null,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
  .placeholder {
    position: absolute;
    background-color: transparent;
    cursor: text;
  }
</style>
